import React, {
  useEffect, useState, useCallback, useRef, SetStateAction, Dispatch, useMemo,
} from 'react';
import {
  Menu, Progress, Button, Popup, PopupProps, Icon,
} from 'semantic-ui-react';
import {
  useAppSelector,
} from 'store';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from 'api';
import util from 'utils/utils';
import useTheme from 'theme/useTheme';

import OneDriveFileChooser from './OneDriveFileChooser';

const allowedTypes = {
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
  csv: 'text/csv',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  xml: 'application/xml',
  xml_text: 'text/xml',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation',
  odg: 'application/vnd.oasis.opendocument.graphics',
  jpg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  tiff: 'image/tiff',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  m2ts: 'video/avchd-stream',
  flv: 'video/x-flv',
};

const ChooserPopup = styled(Popup)`
  z-index:5;
  max-width:400px !important;
  .menu {
    max-width:400px !important;
    min-width:300px !important;
  }
`;

type UploadState = {
  isUploading: boolean;
  uploadProgress: number;
}

type OnCompleteParams = {
  savedName: string;
  fileName: string;
  signedDownloadRequest: string;
}

const uploadFile = (
  file: File | null,
  setUploadState: Dispatch<SetStateAction<UploadState>> = () => {},
  forType: string,
  forId: string,
  onComplete = (..._params) => {},
  onError,
  setOpen: Dispatch<SetStateAction<Boolean>> = () => {},
) => {
  if (file) {
    setUploadState(prevState => ({ ...prevState, isUploading: true, uploadProgress: 1 }));
    const regex = new RegExp('[^a-zA-Z0-9_.]', 'g');
    const name = file.name.replace(regex, '-');
    api.uploads.generateFileUploadRequest({
      forType, forId, name, size: file.size, type: file.type,
    }, (response) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', response.signedRequest);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadState(prevState => ({ ...prevState, uploadProgress: ((e.loaded * 100) / e.total, 10) }));
        }
      });
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          setUploadState(prevState => ({ ...prevState, isUploading: false, uploadProgress: 100 }));
          if (xhr.status === 200) {
            setOpen(false);
            onComplete(response.fileName, name, response.signedDownloadRequest);
          } else if (onError) {
            onError('Unable to upload file');
          }
        }
      };
      xhr.send(file);
    }, (err) => {
      setUploadState(prevState => ({ ...prevState, isUploading: false, uploadProgress: 0 }));
      if (onError) onError(err.message || 'Unable to upload file');
      else toast.error(err.message);
    });
  }
};

const uploadMultiFiles = (
  uploadFiles: FileList,
  setUploadState: Dispatch<SetStateAction<UploadState>> = () => {},
  forType: string,
  forId: string,
  onCompleteMultiple = (..._params) => {},
  onError,
  setOpen: Dispatch<SetStateAction<Boolean>> = () => {},
) => {
  setUploadState(prevState => ({ ...prevState, isUploading: true, uploadProgress: 1 }));
  const regex = new RegExp('[^a-zA-Z0-9_.]', 'g');
  const renamedFiles = Array.from(uploadFiles).map((file) => {
    const name = file.name.replace(regex, '-');
    return {
      name,
      size: file.size,
      type: file.type,
      forType,
      forId,
    };
  });
  api.uploads.generateMultiFileUploadRequest(renamedFiles, response => {
    const { files = [] } = response;
    const uploaders = files.map((file, index) => {
      return new Promise((resolve, reject) => {
        const { request, type } = file;
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', request.signedRequest);
        xhr.setRequestHeader('Content-Type', type);
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadState(prevState => ({ ...prevState, uploadProgress: ((e.loaded * 100) / e.total, 10) }));
          }
        });
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve({
                fileName: request.fileName,
                signedDownloadRequest: request.signedDownloadRequest,
              });
            } else {
              reject('Unable to upload file');
            }
          }
        };
        xhr.send(uploadFiles[index]);
      });
    });
    Promise.all(uploaders)
      .then((
        results: OnCompleteParams[],
      ) => {
        setUploadState(prevState => ({ ...prevState, isUploading: false, uploadProgress: 100 }));
        setOpen(false);
        onCompleteMultiple(results.map((result, index) => ({
          savedName: result.fileName,
          fileName: renamedFiles[index].name,
          signedDownloadRequest: result.signedDownloadRequest,
        })));
      })
      .catch((err) => {
        setUploadState(prevState => ({ ...prevState, isUploading: false, uploadProgress: 0 }));
        if (onError) onError(err.message || 'Unable to upload file');
        else toast.error(err.message);
      });
  }, (err) => {
    setUploadState(prevState => ({ ...prevState, isUploading: false, uploadProgress: 0 }));
    if (onError) onError(err.message || 'Unable to upload file');
    else toast.error(err.message);
  });
}

type FileChooserBaseProps = {
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  trigger?: React.ReactElement;
  content?: string;
  forType: string;
  forId: string;
  onError?: (message: string) => void;
  isUploading?: boolean;
  uploadProgress?: number;
  popupProps?: PopupProps;
  onDragOver?: () => void;
  onDragLeave?: () => void;
}

type FileChooserProps =
  | ({
    allowMultiple?: false;
    onComplete: (
      savedName: OnCompleteParams['savedName'],
      fileName: OnCompleteParams['fileName'],
      signedDownloadRequest: OnCompleteParams['signedDownloadRequest']
    ) => void;
    onCompleteMultiple?: never;
  } & FileChooserBaseProps)
  | ({
    allowMultiple: true;
    onCompleteMultiple: (
      files: OnCompleteParams[]
    ) => void;
    onComplete?: never;
  } & FileChooserBaseProps);

const FileChooser = (props: FileChooserProps) => {
  const { t } = useTranslation();
  const {
    isOpen,
    onOpen,
    onClose,
    trigger,
    content,
    forType,
    forId,
    onError,
    isUploading: propIsUploadLoading,
    uploadProgress: propUploadProgress,
    popupProps = {},
    onDragOver,
    onDragLeave,
    onComplete,
    onCompleteMultiple,
  } = props;

  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    uploadProgress: 0,
  });
  const {
    isUploading: stateIsUploading, uploadProgress: stateUploadProgress,
  } = uploadState;
  const isUploading = (propIsUploadLoading === undefined) ? stateIsUploading : propIsUploadLoading;
  const uploadProgress = (propUploadProgress === undefined) ? stateUploadProgress : propUploadProgress;
  const [open, setOpen] = useState(false);
  const [oneDriveOpen, setOneDriveOpen] = useState(false);
  const inputFile = useRef(null);
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);

  useEffect(() => {
    if (open !== isOpen && (typeof isOpen === 'boolean')) {
      setOpen(!!isOpen);
    }
  }, [open, isOpen]);

  const togglePopup = useCallback(() => {
    if (!open && onOpen) onOpen();
    if (open && onClose) onClose();
    setOpen(prevOpen => !prevOpen);
  }, [open, onOpen, onClose]);

  const storageFileChosen = useCallback((savedName, fileName, signedDownloadRequest) => {
    setOneDriveOpen(false);
    setOpen(false);
    onComplete(savedName, fileName, signedDownloadRequest);
  }, [setOneDriveOpen, setOpen, onComplete]);

  const chooseFile = useCallback(() => {
    inputFile.current.click();
  }, [inputFile]);

  return (
    <React.Fragment>

      <input
        type="file"
        accept={Object.values(allowedTypes).join(',')}
        style={{ display: 'none' }}
        ref={inputFile}
        multiple={props.allowMultiple}
        onChange={e => {
          const files = e.target.files;
          if (files.length === 1 && !props.allowMultiple) {
            uploadFile(files[0], setUploadState, forType, forId, onComplete, onError, setOpen);
          } else if (files.length > 5) {
            toast.error('You can only upload up to 5 files at once');
          } else {
            uploadMultiFiles(files, setUploadState, forType, forId, onCompleteMultiple, onError, setOpen);
          }
        }}
      />

      {/* @ts-ignore */}
      <OneDriveFileChooser
        {...props}
        open={oneDriveOpen}
        onComplete={storageFileChosen}
        onClose={() => {
          setOneDriveOpen(false);
          onClose && onClose();
        }}
      />

      {/* @ts-ignore */}
      <DragAndDropFile
        forType={forType}
        forId={forId}
        allowMultiple={props.allowMultiple}
        onCompleteMultiple={onCompleteMultiple}
        onComplete={onComplete}
        onError={onError}
        setUploadState={setUploadState}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <ChooserPopup
          style={{ zIndex: 999 }}
          on="click"
          open={open}
          position={theme.sizes.isMobile ? 'bottom center' : 'bottom left'}
          onClose={() => onClose ? null : setOpen(false)}
          trigger={(
              trigger
                ? (
                  <div>
                    {React.cloneElement(trigger, {
                      loading: isUploading,
                      onClick: togglePopup,
                      uploadState,
                    })}
                  </div>
                ) : <Button size="small" color="teal" icon="file" fluid content={content || 'Choose a file'} loading={isUploading} onClick={togglePopup} />
          )}
          content={(
            <div>
              <h5 style={{ marginTop: 0 }}>Upload a file</h5>
              <Menu vertical borderless>
                {isUploading && <Progress percent={uploadProgress} indicating progress style={{ margin: 5 }} />}
                <Menu.Item onClick={chooseFile}>
                  <i className="upload icon" />
                  {t('ideas.actions.chooseFile')}
                </Menu.Item>
                {util.userIsConnectedTo(user, 'office365') ? (
                  <Menu.Item onClick={() => setOneDriveOpen(prevOpen => !prevOpen)} icon="microsoft" content="Choose from OneDrive" />
                ) : null}
              </Menu>

              <div style={{ textAlign: 'left', marginTop: 20 }}>
                <Button basic size="tiny" content={t('generic.cancel')} onClick={togglePopup} />
              </div>
            </div>
          )}
          {...popupProps}
        />
      </DragAndDropFile>
    </React.Fragment>
  );
};

type DragAndDropFileBaseProps = {
  forType: string;
  forId: string;
  onDragOver?: () => void;
  onDragLeave?: () => void;
  onError?: (message: string) => void;
  children: React.ReactNode;
  containerStyle?: React.CSSProperties;
  setUploadState: Dispatch<SetStateAction<UploadState>>;
  accept?: string;
  disabled?: boolean;
}

type DragAndDropFileProps =
  | ({
    allowMultiple?: false;
    onComplete: (
      savedName: string,
      fileName: string,
      signedDownloadRequest: string
    ) => void;
    onCompleteMultiple?: never;
  } & DragAndDropFileBaseProps)
  | ({
    allowMultiple: true;
    onCompleteMultiple: (
      files: {
        savedName: string;
        fileName: string;
        signedDownloadRequest: string;
      }[]
    ) => void;
    onComplete?: never;
  } & DragAndDropFileBaseProps);

export const DragAndDropFile = (props: DragAndDropFileProps) => {
  const {
    forType,
    forId,
    onDragOver = () => {},
    onDragLeave = () => {},
    onError,
    children,
    containerStyle,
    setUploadState,
    accept,
    disabled,
    allowMultiple,
    onComplete,
    onCompleteMultiple,
  } = props;
  const [dragItem, setDragItem] = useState(false);
  const dropRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleDragEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getFileFromEvent = useCallback((e) => {
    const files = [...e.dataTransfer.files];
    const filesWithPreview = [];

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      file.preview = URL.createObjectURL(file);
      filesWithPreview.push(file);
    }

    if (allowMultiple) {
      return filesWithPreview;
    }

    return filesWithPreview[0];
  }, [allowMultiple]);

  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    setDragItem(null);
    onDragOver();
  }, [setDragItem, onDragOver, disabled]);

  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    clearTimeout(timeoutRef.current);
    setDragItem(null);
    onDragOver();
  }, [timeoutRef, setDragItem, onDragOver, disabled]);

  const handleDragLeave = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    timeoutRef.current = window.setTimeout(() => { setDragItem(null); onDragLeave(); }, 150);
  }, [timeoutRef, setDragItem, onDragLeave, disabled]);

  const handleDrop = useCallback((e) => {
    if (disabled) return;
    handleDragEvent(e);
    const uploadItem = getFileFromEvent(e);
    if (uploadItem) {
      setDragItem(uploadItem);
      if (accept) {
        const { type } = uploadItem;
        if (accept === 'image/*') {
          if (type.indexOf('image') !== 0) {
            return toast.error('Only images are accepted');
          }
        } else {
          const videoType = type.replace('video/', '.');
          const acceptedTypes = accept.split(',');
          if (acceptedTypes.indexOf(videoType) === -1) {
            return toast.error('Only videos are accepted');
          }
        }
      }
      const file = getFileFromEvent(e);
      if (allowMultiple) {
        uploadMultiFiles(file, setUploadState, forType, forId, onCompleteMultiple, onError);
      } else {
        uploadFile(getFileFromEvent(e), setUploadState, forType, forId, onComplete, onError);
      }
      onDragLeave();
    } else onDragLeave();
  }, [disabled, getFileFromEvent, onDragLeave, accept, allowMultiple, setUploadState, forType, forId, onCompleteMultiple, onError, onComplete]);

  useEffect(() => {
    const dropContainer = dropRef.current;
    dropContainer.addEventListener('dragenter', handleDragEnter);
    dropContainer.addEventListener('dragleave', handleDragLeave);
    dropContainer.addEventListener('dragover', handleDragOver);
    dropContainer.addEventListener('drop', handleDrop);
    return () => {
      dropContainer.removeEventListener('dragenter', handleDragEnter);
      dropContainer.removeEventListener('dragleave', handleDragLeave);
      dropContainer.removeEventListener('dragover', handleDragOver);
      dropContainer.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  useEffect(() => {
    if (dragItem) {
      onDragLeave();
    }
  }, [dragItem, setUploadState, forType, forId, onComplete, onError, onDragLeave]);

  return (
    <div ref={dropRef} style={containerStyle}>
      {children}
    </div>
  );
};

const StyledDropZone = styled.div`
  border: 2px dashed #ccc;
  border-radius: 10px;
  padding: 20px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  > h5 {
    margin: 0;
    display: block;
    color: gray;
  }
  i.icon {
    color: gray;
  }
  u {
    cursor: pointer;
  }
`;

const DroppableFileChooserTrigger = ({
  disabled,
  uploadState,
  isDraggedOver,
  allowMultiple,
  ...rest
} : {
  disabled?: boolean;
  uploadState?: UploadState;
  isDraggedOver?: boolean;
  allowMultiple?: boolean;
}) => {

  const iconName = useMemo(() => {
    if (uploadState?.isUploading) return 'spinner';
    if (isDraggedOver) return 'check circle';
    return 'upload';
  }, [isDraggedOver, uploadState?.isUploading]);

  return (
    <StyledDropZone
      {...rest}
    >
      <Icon
        size='huge'
        name={iconName}
      />
      {uploadState?.isUploading
        ? (
          <Progress
            percent={uploadState.uploadProgress}
            indicating
            progress
            style={{ width: '100%' }}
          >
            Uploading...
          </Progress>
        ) : (
          disabled
            ? (
              <h5>
                Cannot upload more files
              </h5>
            ) : (
              <h5>
                {isDraggedOver
                  ? 'Drop the file to upload'
                  : (
                    <>
                      Drop a file here to upload, or <u>click here to browse</u>.{allowMultiple ? ' You can upload multiple files at once.' : ''}
                    </>
                  )
                }
              </h5>
            )
        )
      }


    </StyledDropZone>
  );
}

export const DroppableFileChooser = (props: FileChooserProps) => {
  const {
    onDragOver: _onDragOver,
    onDragLeave: _onDragLeave,
    trigger: _trigger,
    popupProps = {},
    allowMultiple,
    ...rest 
  } = props;
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  return (
    /* @ts-ignore */
    <FileChooser
      {...rest}
      allowMultiple={allowMultiple}
      onDragOver={() => setIsDraggedOver(true)}
      onDragLeave={() => setIsDraggedOver(false)}
      popupProps={{
        ...popupProps,
        position: 'top center',
        basic: true,
        offset: [0, -100],
      }}
      trigger={(
        <DroppableFileChooserTrigger
          isDraggedOver={isDraggedOver}
          disabled={!!popupProps.disabled}
          allowMultiple={allowMultiple}
          {...rest}
        />
      )}
    />

  );
}

export default FileChooser;
